import type { ExistingRow, GoogleSheetsWriter } from '../clients/googleSheetsClient';
import type { EntityName, SheetRow, SyncSummary } from '../types/entities';
import type { SheetName } from '../types/sheets';

export type SyncEntityOptions<T> = {
  runId: string;
  entity: EntityName;
  sheetName: SheetName;
  headers: string[];
  keyField: string;
  records: T[];
  sheets: GoogleSheetsWriter;
  validate: (record: T) => string[];
  map: (record: T) => Promise<SheetRow>;
  dryRun?: boolean;
  logger?: { info(message: string, meta?: unknown): void; warn(message: string, meta?: unknown): void; error(message: string, meta?: unknown): void };
};

export async function syncEntity<T>(options: SyncEntityOptions<T>): Promise<SyncSummary> {
  const startedAt = new Date().toISOString();
  let createdCount = 0;
  let updatedCount = 0;
  let skippedCount = 0;
  let failedCount = 0;
  const failures: string[] = [];
  const rowsToCreate: SheetRow[] = [];
  const rowsToUpdate: ExistingRow[] = [];

  try {
    await options.sheets.ensureSheetHeaders(options.sheetName, options.headers);
    const existingRows = await options.sheets.getRows(options.sheetName, options.headers);
    const byKey = new Map<string, ExistingRow>();
    existingRows.forEach((row) => {
      const key = String(row.values[options.keyField] || '');
      if (key) byKey.set(key, row);
    });

    for (const record of options.records) {
      const validationErrors = options.validate(record);
      if (validationErrors.length > 0) {
        failedCount += 1;
        failures.push(validationErrors.join('; '));
        options.logger?.warn(`Validation failed for ${options.entity}`, { validationErrors, record });
        continue;
      }

      try {
        const mapped = await options.map(record);
        const key = String(mapped[options.keyField] || '');
        const existing = byKey.get(key);
        if (!existing) {
          rowsToCreate.push(mapped);
          createdCount += 1;
          continue;
        }
        if (existing.values.sync_hash === mapped.sync_hash) {
          skippedCount += 1;
          continue;
        }
        rowsToUpdate.push({ rowNumber: existing.rowNumber, values: mapped });
        updatedCount += 1;
      } catch (error) {
        failedCount += 1;
        failures.push(error instanceof Error ? error.message : String(error));
      }
    }

    if (!options.dryRun) {
      if (rowsToCreate.length > 0) await options.sheets.appendRows(options.sheetName, options.headers, rowsToCreate);
      if (rowsToUpdate.length > 0) await options.sheets.updateRows(options.sheetName, options.headers, rowsToUpdate);
    }

    const finishedAt = new Date().toISOString();
    const status = failedCount > 0 ? 'partial_failure' : 'success';
    return {
      runId: options.runId,
      startedAt,
      finishedAt,
      entity: options.entity,
      status,
      fetchedCount: options.records.length,
      createdCount,
      updatedCount,
      skippedCount,
      failedCount,
      message: `${options.entity}: ${createdCount} created, ${updatedCount} updated, ${skippedCount} skipped, ${failedCount} failed`,
      errorDetails: failures.slice(0, 5).join(' | '),
    };
  } catch (error) {
    const finishedAt = new Date().toISOString();
    const message = error instanceof Error ? error.message : String(error);
    options.logger?.error(`Sync failed for ${options.entity}`, { message });
    return {
      runId: options.runId,
      startedAt,
      finishedAt,
      entity: options.entity,
      status: 'failure',
      fetchedCount: options.records.length,
      createdCount,
      updatedCount,
      skippedCount,
      failedCount: options.records.length || 1,
      message: `${options.entity}: sync failed`,
      errorDetails: message,
    };
  }
}
